import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { IconDownload, IconLoader2 } from '@tabler/icons-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { PromoProductTimeline } from '@/components/spar-mobile/PromoProductTimeline';
import { PromoStateBadge } from '@/components/spar-mobile/PromoStateBadge';
import {
  TimelineRow,
  ProductState,
  ANALYSIS_START_DATE,
  analysisEndDate,
  DAY_WINNER_MIN_UNIT_SHARE,
} from '@/types/promoAttribution';
import { formatPromoNumber } from '@/utils/promoFormatNumber';
import { exportElementToPdf } from '@/utils/exportPdf';

function getState(row: TimelineRow): ProductState {
  if (row.winning_state) return row.winning_state;
  if (row.has_discount && row.has_promo_data) return 'Promotion and Promo Data';
  if (!row.has_discount && row.has_promo_data) return 'Promo Data';
  if (row.has_discount && !row.has_promo_data) return 'Promotion';
  return 'Standard Price';
}

// "Promo Data" alone is intentionally excluded from the per-state stat cards.
// It's the rare case where the promo data tag fired but no till discount ran,
// and in practice it has only a handful of days per product — too few to make
// the "% vs standard" comparison statistically meaningful (we were displaying
// things like "−64% vs standard" computed from 2 days). The state still exists
// in the timeline + daily breakdown for raw inspection.
const STATE_ORDER: ProductState[] = [
  'Standard Price',
  'Promotion',
  'Promotion and Promo Data',
  'Mixed',
];

export function PromoAttributionProductDetailPage() {
  const { gtin } = useParams<{ gtin: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<TimelineRow[]>([]);
  const [description, setDescription] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  const displayRange = `${new Date(ANALYSIS_START_DATE + 'T12:00:00Z').toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })} → ${new Date(analysisEndDate() + 'T12:00:00Z').toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' })}`;

  const fetchData = useCallback(async () => {
    if (!gtin) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/spar-mobile/promo-attribution/product-timeline/${gtin}`);
      const payload = res.data.data ?? {};
      const raw: Record<string, unknown>[] = Array.isArray(payload)
        ? payload
        : (payload.timeline ?? []);
      const desc: string | null = Array.isArray(payload)
        ? null
        : (payload.description ?? null);

      // Recursively unwrap any BigQuery-style { value: ... } wrapper objects
      const deepNormalize = (val: unknown): unknown => {
        if (val === null || val === undefined) return val;
        if (typeof val === 'object' && 'value' in (val as object)) {
          return deepNormalize((val as Record<string, unknown>).value);
        }
        return val;
      };

      const normalized = raw.map((row) => {
        const out: Record<string, unknown> = {};
        for (const key of Object.keys(row)) {
          out[key] = deepNormalize(row[key]);
        }
        return out;
      });
      setData(normalized as unknown as TimelineRow[]);
      setDescription(desc);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [gtin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExportPdf = async () => {
    if (!printRef.current || !gtin) return;
    setExporting(true);
    try {
      const date = new Date().toISOString().slice(0, 10);
      await exportElementToPdf(printRef.current, {
        filename: `promo-attribution-${gtin}-${date}.pdf`,
      });
    } catch (err) {
      console.error('PDF export failed', err);
      toast.error('Could not generate PDF', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setExporting(false);
    }
  };

  const stateCounts = data.reduce<Record<string, { days: number; units: number }>>((acc, row) => {
    const s = getState(row);
    if (!acc[s]) acc[s] = { days: 0, units: 0 };
    acc[s].days += 1;
    acc[s].units += row.units_sold;
    return acc;
  }, {});
  const baseline = stateCounts['Standard Price'];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2 justify-end">
        <button
          onClick={handleExportPdf}
          disabled={exporting || data.length === 0}
          className="inline-flex items-center gap-1.5 rounded-md border border-input bg-background px-3 py-1.5 text-sm hover:bg-muted/40 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {exporting
            ? <IconLoader2 size={14} className="animate-spin" />
            : <IconDownload size={14} />}
          {exporting ? 'Generating…' : 'Export PDF'}
        </button>
        <button
          onClick={() => navigate('/spar-mobile/promo-attribution/products')}
          className="flex items-center gap-1.5 text-sm text-muted-foreground border border-input rounded-md px-3 py-1.5 hover:bg-muted/40"
        >
          ← Product Explorer
        </button>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <div ref={printRef} className="space-y-6 bg-background">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {description ?? <span className="font-mono">{gtin}</span>}
        </h1>
        {description && (
          <p className="text-sm text-muted-foreground font-mono mt-0.5">{gtin}</p>
        )}
        <p className="text-xs text-muted-foreground mt-1">Analysis window: {displayRange}</p>
      </div>

      {Object.keys(stateCounts).length > 0 && (
        // auto-fit + 1fr keeps however many cards render evenly distributed
        // across the row instead of leaving gaps when fewer states appear (e.g.
        // products with no Promotion days, or "Promo Data" alone being hidden).
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}
        >
          {STATE_ORDER.map((state) => {
            const sc = stateCounts[state];
            if (!sc) return null;
            const avgUnits = sc.units / sc.days;
            const uplift = baseline
              ? ((avgUnits - baseline.units / baseline.days) / (baseline.units / baseline.days)) * 100
              : null;
            return (
              <div key={state} className="rounded-lg border bg-card p-5 space-y-1">
                <PromoStateBadge state={state} />
                <p className="text-3xl font-bold tabular-nums">{formatPromoNumber(avgUnits, 1)}</p>
                <p className="text-xs text-muted-foreground">avg daily units</p>
                {uplift != null && state !== 'Standard Price' && (
                  <p className={cn('text-xs font-semibold', uplift >= 0 ? 'text-emerald-600' : 'text-red-500')}>
                    {uplift >= 0 ? '+' : ''}{formatPromoNumber(uplift, 1)}% vs standard
                  </p>
                )}
                <p className="text-xs text-muted-foreground">{formatPromoNumber(sc.days)} days · {formatPromoNumber(sc.units)} total units</p>
              </div>
            );
          })}
        </div>
      )}

      <div className="rounded-lg border bg-card">
        <div className="p-4 pb-2">
          <p className="text-base font-semibold">Daily Sales Timeline</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Bar colour: grey = Standard · amber = Promotion · blue = Promo Data · purple = Both · slate = Mixed
            (no single state ≥ {Math.round(DAY_WINNER_MIN_UNIT_SHARE * 100)}% of units). Hover a day for the full unit mix.
          </p>
        </div>
        <div className="p-4 pt-0">
          {data.length > 0 ? (
            <PromoProductTimeline data={data} />
          ) : (
            <div className="h-48 flex items-center justify-center text-muted-foreground text-sm">
              {loading ? 'Loading…' : 'No sales data found.'}
            </div>
          )}
        </div>
      </div>

      {data.length > 0 && (
        <>
          <hr />
          <div>
            <h2 className="text-lg font-semibold tracking-tight mb-4">Daily Breakdown</h2>
            <div className="rounded-lg border bg-card overflow-hidden">
              <div className="max-h-96 overflow-y-auto" data-pdf-expand>
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-card border-b">
                    <tr className="text-left text-xs text-muted-foreground">
                      <th className="px-4 py-2 font-medium">Date</th>
                      <th className="px-4 py-2 font-medium">State</th>
                      <th className="px-4 py-2 font-medium text-right">Units Sold</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...data].reverse().map((row, idx) => {
                      const saleDate = typeof row.sale_date === 'object' && row.sale_date !== null
                        ? String((row.sale_date as Record<string, unknown>).value ?? '')
                        : String(row.sale_date ?? '');
                      return (
                        <tr key={`${saleDate}-${idx}`} className="border-t">
                          <td className="px-4 py-2 font-mono text-sm text-muted-foreground">{saleDate}</td>
                          <td className="px-4 py-2"><PromoStateBadge state={getState(row)} /></td>
                          <td className="px-4 py-2 text-right">{formatPromoNumber(Number(row.units_sold))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
      </div>
    </div>
  );
}
